import type { CompanyDashboardLinks } from "@/dashboards/companyDashboardsDomain";
import { accessLabels, AccessType } from "@/types/enums/AccessType";
import { IconName } from "@/components-v2/iconMapping";
import { Impersonation, User } from "@/types/UserInfo";
import { getFullName } from "@/utils/Roles";
import {
  adminLinks,
  boardMemberLinks,
  HeaderLink,
  optionHolderLinks,
  shareholderLinks,
  SubLink,
} from "./headerLinks";
import { CompanyItem, Portfolio, SignDocumentCount, SigningMethod } from "@/common/common";
import { Language } from "@/i18n/languages";
import {
  EnterpriseAccountItem,
  extractPortfolioBaseUrl,
  extractPortfolioCompaniesUrl,
  extractPortfolioContactsUrl,
  extractPortfolioFollowUpCompanyInfoUrl,
  extractPortfolioFollowUpMattersUrl,
  extractPortfolioFollowUpShareholdersUrl,
  extractPortfolioFollowUpSignaturesUrl,
  PortfolioHeader,
  SelectedPortfolioOption,
  showPortfolioContacts,
} from "@/portfolio/portfolioDomain";
import { TMessage } from "@/i18n/tt";
import { UserPrefs } from "@/stores/userPrefsDomain";
import { PaidSubscription } from "@/common/subscription";
import { CustomRole, CustomRoleForUser } from "@/common/customRole";
import { OwnershipModel } from "@/companyRegistry/companyRegistryApiClient";
import { getRoleNameByLanguage } from "@/portfolio/contacts/customRoleDomain";
import { stripQueryString } from "@/utils/queryParams";
import { DCEvent } from "@/events/eventsDomain";
import { ReactNode } from "react";
import { compact } from "lodash";
import { TMessageKey } from "@/i18n/types.gen";
import { AlertMessage } from "@/stores/AlertMessageStore";
import { FeatureToggles, PortfolioFeatureToggles } from "@/common/featureToggles";
import {
  appBarCompanyStatusLabels,
  appBarCompanyStatusSinceLabels,
  CompanyStatus,
} from "@/types/CompanyInfo";
import { CompanyStatusChangeDTO } from "@/types/Transactions";
import {
  enterpriseAccountUrl,
  NavigationState,
  navigationState,
  portfolioReportsUrl,
  portfolioSearchUrl,
  superadminUrl,
} from "@/utils/navigation";

export type AppBarActions = {
  addAdministrator?: (companyItem: CompanyItem) => void;
  changeLanguage?: (language: Language) => void;
  logIn?: () => void;
  about?: () => void;
  logOut?: () => void;
  showContactSaleAndSupport?: boolean;
  showPricesLink?: boolean;
  navigateToUserSettings?: () => void;
  navigateToFeedback?: () => void;
  openCompanySettings?: () => void;
  openPaymentDialog?: DCEvent[];
  openHelpCenter?: () => void;
  openCookieSettings?: () => void;
  selectCompany?: (company: {
    organisationNumber: string;
    accessType: AccessType;
    shareholderId?: number;
  }) => void;
  upgradeToPro?: () => void;
  filterCompaniesInPortfolio?: (type: SelectedPortfolioOption) => void;
  openCreatePortfolioDialog?: () => void;
  openPortfolio?: (portfolio: Portfolio) => void;
  openPortfolioSettings?: () => void;
  openBatchSigningPage?: (signingMethod: SigningMethod) => void;
};

export type AppBarSubLink = {
  url: string;
  exact?: boolean;
  text: TMessage;
  isActive: boolean;
  tag?: SubLink["tag"];
  badge?: SubLink["badge"];
  badgeColor?: SubLink["badgeColor"];
};

export type AppBarLink = {
  url: string;
  exact?: boolean;
  text: TMessage;
  icon: IconName;
  isActive: boolean;
  subLinks?: AppBarSubLink[];
  tag?: TMessage;
  tagColor?: HeaderLink["tagColor"];
  badge?: HeaderLink["badge"];
  badgeColor?: HeaderLink["badgeColor"];
};

export type CompanyOption = {
  organisationNumber: string;
  name: string;
  shareholderId?: number;
  accessType: AccessType;
  role?: CustomRole;
};

export const accessTagLabel = (
  company: CompanyOption,
  language: Language,
  labels: { [key in AccessType]: TMessageKey } = accessLabels,
): TMessage =>
  company.accessType === AccessType.CUSTOM && company.role
    ? { translated: getRoleNameByLanguage(company.role, language) }
    : { key: labels[company.accessType] };

export type AppBarData = {
  languages: { language: Language; isSelected?: boolean }[];
  user: {
    name: string;
    company?: string;
    initials: string;
    impersonation?: Impersonation;
    email?: string;
  };
  userMainAction?: ReactNode;
  portfolioUrl: string;
  backToPortfolioLabel?: TMessage;
  links?: AppBarLink[];
  subLinks?: AppBarSubLink[];
  helpCenter?: AppBarLink;
  companies?: CompanyOption[];
  portfolios?: Portfolio[];
  portfolioHeader?: PortfolioHeader;
  selectedCompany?: CompanyOption;
  selectedPortfolioOption?: SelectedPortfolioOption;
  subscription?: PaidSubscription;
  signDocumentCounts?: SignDocumentCount[];
  actions: AppBarActions;
  displayUserName?: boolean;
  notification?: AlertMessage;
  liquidationLabel?: TMessage;
  liquidationInfoText?: TMessage;
  mergedLabel?: TMessage;
  mergedInfoText?: TMessage;
  companyStatusLabel?: TMessage;
  companyStatusInfoText?: TMessage;
  accountMenuLinks?: AccountMenuLink[];
};

export type AccountMenuLink = {
  type: "enterprise-account" | "superadmin";
  label: TMessage;
  url: string;
  state?: NavigationState;
  icon: IconName;
};

export function accountMenuLinks(
  enterpriseAccountItems: EnterpriseAccountItem[],
  hasSuperadminAccess: boolean,
  currentUrl: string,
): AccountMenuLink[] {
  const accountLinks = enterpriseAccountItems.map<AccountMenuLink>((item) => ({
    type: "enterprise-account",
    label: { translated: item.name },
    url: enterpriseAccountUrl(item.id),
    state: navigationState({ referrer: currentUrl }),
    icon: "building",
  }));
  const superadminLink: AccountMenuLink = {
    type: "superadmin",
    label: { key: "portfolio.SUPERADMIN" },
    url: superadminUrl,
    icon: "detective",
  };
  return hasSuperadminAccess ? [...accountLinks, superadminLink] : accountLinks;
}

export function getTotalSignDocumentCount(signDocumentCounts?: SignDocumentCount[]) {
  return signDocumentCounts ? signDocumentCounts.reduce((acc, { count }) => acc + count, 0) : 0;
}

export function groupBySigningMethod(
  signDocumentCounts?: SignDocumentCount[],
): { signingMethod: SigningMethod; count: number }[] {
  if (!signDocumentCounts) return [];
  const grouped = signDocumentCounts.reduce(
    (acc, item) => {
      acc[item.signingMethod] = (acc[item.signingMethod] || 0) + item.count;
      return acc;
    },
    {} as { [key in SigningMethod]: number },
  );

  return Object.entries(grouped).map(([signingMethod, count]) => ({
    signingMethod: signingMethod as SigningMethod,
    count,
  }));
}

export function prepLanguages(current: Language): AppBarData["languages"] {
  return Object.values(Language).map((l) => ({
    language: l,
    isSelected: l === current,
  }));
}

export function prepUser(user: User): AppBarData["user"] {
  return {
    initials: (user.firstName?.slice(0, 1) ?? "") + user.lastName.slice(0, 1).toLocaleUpperCase(),
    name: getFullName(user),
    impersonation: user.impersonation,
  };
}

export function getMenuLinks({
  accessType,
  orgNumber,
  isMobile,
  portfolioUrl,
  shareholderId,
  country,
  subscription,
  userRole,
  followUpCount,
  featureToggles,
  shareholderHasOptions,
  shareholderIsOptionHolderOnly,
  ownershipModel,
  showShareClasses,
  companyDashboards,
}: {
  orgNumber: string;
  accessType: AccessType | null;
  isMobile: boolean;
  portfolioUrl: string;
  shareholderId?: number;
  country?: string;
  subscription?: PaidSubscription;
  userRole?: CustomRoleForUser;
  followUpCount?: number;
  featureToggles: FeatureToggles;
  shareholderHasOptions?: boolean;
  shareholderIsOptionHolderOnly?: boolean;
  ownershipModel?: OwnershipModel;
  showShareClasses?: boolean;
  companyDashboards?: CompanyDashboardLinks;
}): HeaderLink[] {
  const portfolio: HeaderLink[] = isMobile
    ? [
        {
          to: portfolioUrl,
          basePath: portfolioUrl,
          text: { key: "header.portfolio" },
          exact: true,
          icon: "briefcase",
        },
      ]
    : [];
  if (
    accessType !== AccessType.BOARD_MEMBER &&
    accessType !== AccessType.SHAREHOLDER &&
    accessType !== AccessType.ADDITIONAL_REPRESENTATIVE &&
    userRole !== undefined &&
    userRole.customRoleId !== "all"
  ) {
    return portfolio.concat(
      adminLinks({
        orgNo: orgNumber,
        country,
        subscription,
        userRole,
        featureToggles,
        ownershipModel,
        showShareClasses,
        companyDashboards,
      }),
    );
  } else if (accessType === AccessType.ADMIN) {
    return portfolio.concat(
      adminLinks({
        orgNo: orgNumber,
        country,
        subscription,
        followUpCount,
        featureToggles,
        ownershipModel,
        showShareClasses,
        companyDashboards,
      }),
    );
  } else if (accessType === AccessType.BOARD_MEMBER) {
    return portfolio.concat(boardMemberLinks(orgNumber));
  } else if (
    (accessType === AccessType.SHAREHOLDER ||
      accessType === AccessType.ADDITIONAL_REPRESENTATIVE) &&
    shareholderId
  ) {
    return portfolio.concat(
      shareholderIsOptionHolderOnly
        ? optionHolderLinks(orgNumber, shareholderId)
        : shareholderLinks(orgNumber, shareholderId, featureToggles, shareholderHasOptions),
    );
  }
  return [];
}

function isActive({ to, exact }: { to: string; exact?: boolean }, currentUrl: string) {
  const actualTo = stripQueryString(to);
  if (exact) {
    return actualTo === currentUrl;
  }
  return currentUrl.includes(actualTo);
}

export function prepMenuLinks(params: {
  orgNumber: string;
  accessType: AccessType | null;
  currentUrl: string;
  isMobile: boolean;
  portfolioUrl: string;
  shareholderId?: number;
  country?: string;
  subscription?: PaidSubscription;
  userPrefs: UserPrefs;
  userRole?: CustomRoleForUser;
  followUpCount?: number;
  featureToggles: FeatureToggles;
  shareholderHasOptions?: boolean;
  shareholderIsOptionHolderOnly?: boolean;
  ownershipModel?: OwnershipModel;
  showShareClasses?: boolean;
  companyDashboards?: CompanyDashboardLinks;
}): AppBarLink[] {
  const { currentUrl } = params;
  return getMenuLinks(params).map((l) => {
    const subLinks = l.subLinks?.map<AppBarSubLink>((sl) => ({
      url: sl.to,
      exact: sl.exact,
      text: sl.text,
      badge: sl.badge,
      badgeColor: sl.badgeColor,
      isActive: isActive(sl, currentUrl),
      tag: sl.tag,
    }));
    return {
      url: l.to,
      exact: l.exact,
      icon: l.icon,
      text: l.text,
      badge: l.badge,
      badgeColor: l.badgeColor,
      tag: l.tag,
      tagColor: l.tagColor,
      isActive: isActive(l, currentUrl) || subLinks?.some((sl) => sl.isActive) || false,
      subLinks,
    };
  });
}

export function prepFollowUpsLink({
  currentUrl,
  portfolio,
  portfolioFollowUpCount,
}: {
  currentUrl: string;
  portfolio: SelectedPortfolioOption;
  portfolioFollowUpCount: number;
}): AppBarLink {
  const signaturesUrl = extractPortfolioFollowUpSignaturesUrl(portfolio);
  const mattersUrl = extractPortfolioFollowUpMattersUrl(portfolio);
  const companyInfoUrl = extractPortfolioFollowUpCompanyInfoUrl(portfolio);
  const shareholdersUrl = extractPortfolioFollowUpShareholdersUrl(portfolio);
  return {
    url: signaturesUrl,
    exact: true,
    icon: "clipboard-text",
    text: { key: "header.follow-up" },
    isActive:
      isActive({ to: mattersUrl, exact: false }, currentUrl) ||
      isActive({ to: signaturesUrl, exact: false }, currentUrl) ||
      isActive({ to: companyInfoUrl, exact: false }, currentUrl) ||
      isActive({ to: shareholdersUrl, exact: false }, currentUrl),
    tag: portfolioFollowUpCount > 0 ? { translated: String(portfolioFollowUpCount) } : undefined,
  };
}

export function prepContactsLink({
  currentUrl,
  portfolio,
}: {
  currentUrl: string;
  portfolio: SelectedPortfolioOption;
}): AppBarLink {
  const url = extractPortfolioContactsUrl(portfolio);
  return {
    url,
    exact: true,
    icon: "user-list",
    text: { key: "header.contacts" },
    isActive: isActive({ to: "/contacts", exact: false }, currentUrl),
  };
}

export function prepHelpCenterLink({
  currentUrl,
  icon,
  portfolio,
}: {
  currentUrl: string;
  icon?: IconName;
  portfolio?: SelectedPortfolioOption;
}): AppBarLink {
  const baseUrl = portfolio ? extractPortfolioBaseUrl(portfolio) : "";
  return {
    url: `${baseUrl}/help-center`,
    icon: icon ?? "rocket",
    text: { key: "header.help-center" },
    isActive: isActive({ to: `${baseUrl}/help-center` }, currentUrl),
  };
}

export function prepPortfolioSearchLink(currentUrl: string): AppBarLink {
  return {
    url: portfolioSearchUrl,
    exact: true,
    icon: "house",
    text: { key: "header.home" },
    isActive: isActive({ to: portfolioSearchUrl }, currentUrl),
  };
}

export function prepPortfolioReportsLink(currentUrl: string): AppBarLink {
  return {
    url: portfolioReportsUrl,
    exact: true,
    icon: "table",
    text: { key: "header.reports" },
    isActive: isActive({ to: portfolioReportsUrl }, currentUrl),
  };
}

export function prepPortfolioLinks({
  currentUrl,
  portfolio,
  portfolioFollowUpCount,
  showContacts,
  portfolioFeatureToggles,
}: {
  currentUrl: string;
  portfolio: SelectedPortfolioOption;
  portfolioFollowUpCount: number;
  showContacts: boolean;
  portfolioFeatureToggles: PortfolioFeatureToggles;
}): AppBarLink[] {
  return compact([
    portfolioFeatureToggles.search && prepPortfolioSearchLink(currentUrl),
    prepPortfolioCompaniesLink({ currentUrl, portfolio }),
    prepFollowUpsLink({ currentUrl, portfolio, portfolioFollowUpCount }),
    showContacts && showPortfolioContacts(portfolio) && prepContactsLink({ currentUrl, portfolio }),
    portfolioFeatureToggles.reports && prepPortfolioReportsLink(currentUrl),
    showContacts && prepHelpCenterLink({ currentUrl, portfolio }),
  ]);
}

export function prepPortfolioCompaniesLink({
  currentUrl,
  portfolio,
}: {
  currentUrl: string;
  portfolio: SelectedPortfolioOption;
}): AppBarLink {
  const url = `${extractPortfolioCompaniesUrl(portfolio)}`;
  return {
    url,
    exact: true,
    icon: "buildings",
    text: { key: "header.companies" },
    isActive: isActive({ to: url, exact: false }, currentUrl),
  };
}

export function companyStatusHeading(
  status: CompanyStatus | undefined,
  change: CompanyStatusChangeDTO | undefined,
): { label?: TMessage; infoText?: TMessage } {
  const sinceKey = change && appBarCompanyStatusSinceLabels[change.status];
  if (change && sinceKey) {
    return {
      label: { key: sinceKey, params: { date: { type: "date", value: change.transactionDate } } },
      infoText: change.comment ? { translated: change.comment } : undefined,
    };
  }
  const key = status && appBarCompanyStatusLabels[status];
  return { label: key ? { key } : undefined };
}
